import { useTranslation } from 'react-i18next';
import { useQueryClient } from 'react-query';
import { RequiredAuthType } from 'src/components/auth';
import { StyledIcon } from 'src/components/foundations/foundations.styles';
import { handleErrorToast } from 'src/components/molecules';
import { useDeleteSessionMutation } from 'src/graphql';
import { boot, shutdown } from 'src/utils/intercom';
import { useTheme } from 'styled-components';
import I18next from '../../../i18n';
import * as Types from '../../../types';
import { NavbarVariantEnum } from '../../../types';
import {
  BodyText,
  ClickableContainer,
  ClickableNavigation,
  Logout,
  MenuTooltip,
  Title,
} from './SettingsTooltip.styles';

interface Props {
  root: HTMLElement;
  closeTooltip: () => void;
  onLogout: () => void;
  variant?: NavbarVariantEnum;
  settingsOptions?: {
    text: string;
    linkTo: string;
    logout: boolean;
    iconClassName: string;
  }[];
  customPasswordLink?: string;
}

export const SettingsTooltip = (props: Props) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const queryClient = useQueryClient();
  const variant = props.variant ?? 'manager';
  const utmSource = (() => {
    switch (variant) {
      case NavbarVariantEnum.REPORTER:
        return 'rephub';
      case NavbarVariantEnum.PUBLIC:
        return 'public';
      default:
        return 'reshub';
    }
  })();

  const logout = useDeleteSessionMutation({
    onSuccess: (data) => {
      queryClient.removeQueries();
      const d1pLogoutUrl = data?.deleteSession?.d1pLogoutUrl;
      if (d1pLogoutUrl && variant !== NavbarVariantEnum.PUBLIC) {
        window.location.href = d1pLogoutUrl;
        return;
      }
      props.onLogout();
    },
    onError: () => {
      handleErrorToast({ toastMessage: 'Could not delete session.' });
    },
  });
  const { settingsOptions } = props;
  const hasCustomOptions = Boolean(settingsOptions);
  const iconColor = theme.isVaultVariant
    ? theme.newPalette.neutral.N900
    : theme.atlas.core.color.gray[950];

  return (
    <MenuTooltip
      variant="down"
      root={props.root}
      width="230px"
      data-testid="settings-tooltip"
    >
      <Title>{t('general.navbar.title')}</Title>
      {!hasCustomOptions && (
        <ClickableNavigation
          to={
            variant === NavbarVariantEnum.REPORTER
              ? '/reporting/settings'
              : '/case-settings'
          }
          onClick={props.closeTooltip}
        >
          <StyledIcon
            className="uil uil-suitcase"
            size="20px"
            color={iconColor}
          />
          <BodyText>{t('general.navbar.settings')}</BodyText>
        </ClickableNavigation>
      )}
      <RequiredAuthType
        authTypes={[
          Types.AuthenticationType.VAULT_LOGIN,
          Types.AuthenticationType.VAULT_2FA,
        ]}
      >
        <ClickableNavigation
          to={props.customPasswordLink ?? 'change-password'}
          onClick={props.closeTooltip}
          data-testid="settings-tooltip-change-password"
        >
          <StyledIcon
            className="uil uil-lock-alt"
            size="20px"
            color={iconColor}
          />
          <BodyText>{t('general.navbar.changePassword')}</BodyText>
        </ClickableNavigation>
      </RequiredAuthType>
      {props.settingsOptions?.map((option) => (
        <ClickableNavigation
          to={option.linkTo}
          onClick={props.closeTooltip}
          key={option.text}
        >
          <StyledIcon
            className={option.iconClassName}
            size="20px"
            color={iconColor}
          />
          <BodyText>{option.text}</BodyText>
        </ClickableNavigation>
      ))}
      <ClickableContainer
        onClick={() => {
          window.open(
            I18next.language?.startsWith('zh')
              ? `https://vaultplatform.com/product-privacy-statement-chinese/?utm_source=${utmSource}`
              : `https://vaultplatform.com/product-privacy-statement-web/?utm_source=${utmSource}`,
            '_blank',
            'noopener,noreferrer',
          );
          props.closeTooltip();
        }}
      >
        <StyledIcon className="uil uil-shield" size="20px" color={iconColor} />
        <BodyText>{t('general.navbar.privacyStatement')}</BodyText>
      </ClickableContainer>
      <ClickableContainer
        onClick={async () => {
          shutdown();
          await logout.mutate({});
          boot();
        }}
        data-testid="logout-button"
      >
        <Logout>{t('general.navbar.logOut')}</Logout>
      </ClickableContainer>
    </MenuTooltip>
  );
};
